// Runtime helpers ported from thoughtful-oasis-v8.2/script.js.
// Each initializer is idempotent — safe to call on every route mount.

export function initRevealOnScroll(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll<HTMLElement>('.reveal').forEach((el) => el.classList.add('is-visible'));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-visible');
          io.unobserve(e.target);
        }
      });
    },
    { rootMargin: '0px 0px -10% 0px' }
  );
  document.querySelectorAll<HTMLElement>('.reveal').forEach((el) => {
    if (el.dataset.revealBound === '1') return;
    el.dataset.revealBound = '1';
    io.observe(el);
  });
}

// Nav strip open/close state is now fully driven by React in Nav.tsx (both
// hover and click paths, plus outside-click and Escape handling). Kept as a
// no-op so callers of initWebsiteRuntime() don't need to change.
export function initNavStrip(): void {
  return;
}

export function initCite(): void {
  if (typeof document === 'undefined') return;

  function getData(cite: Element): any {
    const script = cite.querySelector('script.cite__data');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent || '{}');
    } catch {
      return null;
    }
  }
  function download(filename: string, text: string, mime: string) {
    const blob = new Blob([text], { type: mime + ';charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  function confirmCopy(btn: HTMLElement, label: string) {
    const original = btn.textContent;
    btn.textContent = label;
    btn.classList.add('is-confirmed');
    setTimeout(() => {
      btn.textContent = original;
      btn.classList.remove('is-confirmed');
    }, 1400);
  }

  document.querySelectorAll<HTMLElement>('.cite').forEach((cite) => {
    if (cite.dataset.citeBound === '1') return;
    cite.dataset.citeBound = '1';
    cite.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const btn = target.closest<HTMLElement>('.cite__item');
      if (!btn) return;
      const data = getData(cite);
      if (!data) return;
      const format = btn.dataset.format!;
      const action = btn.dataset.action!;
      const text = data[format];
      if (!text) return;
      if (action === 'copy') {
        const done = () => confirmCopy(btn, 'Copied');
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard
            .writeText(text)
            .then(done)
            .catch(() => {
              const ta = document.createElement('textarea');
              ta.value = text;
              document.body.appendChild(ta);
              ta.select();
              try {
                document.execCommand('copy');
                done();
              } catch {
                /* noop */
              }
              document.body.removeChild(ta);
            });
        }
      } else if (action === 'download') {
        const ext = format === 'bibtex' ? '.bib' : '.txt';
        const mime = format === 'bibtex' ? 'application/x-bibtex' : 'text/plain';
        download(data.filename + ext, text, mime);
        confirmCopy(btn, 'Downloaded');
      }
    });
    document.addEventListener('click', (e) => {
      const det = cite as HTMLDetailsElement;
      if (!det.open) return;
      if (!cite.contains(e.target as Node)) det.open = false;
    });
  });
}

export function initForceDownload(): void {
  if (typeof document === 'undefined') return;
  if ((document as any).__forceDownloadBound) return;
  (document as any).__forceDownloadBound = true;
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const trigger = target.closest<HTMLElement>('[data-download-source]');
    if (!trigger) return;
    e.preventDefault();
    const sourceId = trigger.getAttribute('data-download-source');
    if (!sourceId) return;
    const source = document.getElementById(sourceId);
    if (!source) return;
    const filename = trigger.getAttribute('data-filename') || sourceId;
    const mime = trigger.getAttribute('data-mime') || 'application/octet-stream';
    const blob = new Blob([source.textContent || ''], { type: mime + ';charset=utf-8' });
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
  });
}

export function initWebsiteRuntime(): void {
  initRevealOnScroll();
  initNavStrip();
  initCite();
  initForceDownload();
}
